import { useState } from 'react';
import {
  dialCode,
  examplePhone,
  flagOf,
  formatNational,
  toE164,
  TOP_COUNTRIES,
  type CountryCode,
} from '../kiosk/phone';
import type { AppLang } from './ClientApp';

/**
 * Sign-in by phone + SMS code (owner 26–27.10.2026: «вход по телефону, SMS-код, 90 дней»; most
 * clients do not use e-mail). The server asks Twilio Verify to send and check the code; the app
 * keeps the session it gets back. «Sign in with e-mail» stays as a fallback.
 */
const T = {
  pt: {
    title: 'As minhas marcações',
    phone: 'O seu telemóvel',
    hint: 'Use o número da sua marcação. Enviamos um código por SMS.',
    send: 'Enviar código',
    sending: 'A enviar…',
    codeTitle: 'Introduza o código',
    codeHint: (p: string) => `Se ${p} tiver marcações connosco, o código chega por SMS dentro de um minuto.`,
    code: 'Código do SMS',
    show: 'Entrar',
    checking: 'A verificar…',
    other: 'Outro número',
    again: 'Reenviar',
    email: 'Entrar com e-mail',
    bad: 'Verifique o número.',
    wrong: 'O código não está certo ou expirou.',
    many: 'Demasiadas tentativas — tente daqui a uns minutos.',
    fail: 'Algo correu mal — tente de novo.',
  },
  en: {
    title: 'My bookings',
    phone: 'Your mobile number',
    hint: 'Use the number of your booking. We will send you a code by SMS.',
    send: 'Send code',
    sending: 'Sending…',
    codeTitle: 'Enter the code',
    codeHint: (p: string) => `If ${p} has bookings with us, the code arrives by SMS within a minute.`,
    code: 'SMS code',
    show: 'Sign in',
    checking: 'Checking…',
    other: 'Other number',
    again: 'Send again',
    email: 'Sign in with e-mail',
    bad: 'Please check the number.',
    wrong: 'The code is not right or has expired.',
    many: 'Too many attempts — try again in a few minutes.',
    fail: 'Something went wrong — please try again.',
  },
  fr: {
    title: 'Mes rendez-vous',
    phone: 'Votre mobile',
    hint: 'Utilisez le numéro de votre réservation. Nous envoyons un code par SMS.',
    send: 'Envoyer le code',
    sending: 'Envoi…',
    codeTitle: 'Saisissez le code',
    codeHint: (p: string) => `Si ${p} a des rendez-vous chez nous, le code arrive par SMS en une minute.`,
    code: 'Code SMS',
    show: 'Se connecter',
    checking: 'Vérification…',
    other: 'Autre numéro',
    again: 'Renvoyer',
    email: 'Se connecter par e-mail',
    bad: 'Vérifiez le numéro.',
    wrong: 'Le code est incorrect ou a expiré.',
    many: 'Trop d’essais — réessayez dans quelques minutes.',
    fail: 'Un problème est survenu — réessayez.',
  },
  es: {
    title: 'Mis citas',
    phone: 'Su móvil',
    hint: 'Use el número de su reserva. Le enviamos un código por SMS.',
    send: 'Enviar código',
    sending: 'Enviando…',
    codeTitle: 'Introduzca el código',
    codeHint: (p: string) => `Si ${p} tiene citas con nosotros, el código llega por SMS en un minuto.`,
    code: 'Código SMS',
    show: 'Entrar',
    checking: 'Comprobando…',
    other: 'Otro número',
    again: 'Reenviar',
    email: 'Entrar con e-mail',
    bad: 'Compruebe el número.',
    wrong: 'El código no es correcto o ha caducado.',
    many: 'Demasiados intentos — inténtelo en unos minutos.',
    fail: 'Algo ha fallado — inténtelo de nuevo.',
  },
  ru: {
    title: 'Мои записи',
    phone: 'Ваш мобильный',
    hint: 'Укажите номер, с которым вы записывались. Мы пришлём код по SMS.',
    send: 'Отправить код',
    sending: 'Отправляем…',
    codeTitle: 'Введите код',
    codeHint: (p: string) => `Если на номер ${p} есть записи, код придёт по SMS в течение минуты.`,
    code: 'Код из SMS',
    show: 'Войти',
    checking: 'Проверяем…',
    other: 'Другой номер',
    again: 'Отправить ещё раз',
    email: 'Войти по e-mail',
    bad: 'Проверьте номер.',
    wrong: 'Код неверный или устарел.',
    many: 'Слишком много попыток — попробуйте через несколько минут.',
    fail: 'Что-то пошло не так — попробуйте ещё раз.',
  },
} as const;

type Step = { kind: 'phone' } | { kind: 'code'; phone: string; resent?: boolean };

export function PhoneSignIn({
  lang,
  fetchImpl,
  onToken,
  onEmail,
}: {
  lang: AppLang;
  fetchImpl: typeof fetch;
  onToken: (token: string) => void;
  onEmail: () => void;
}) {
  const t = T[lang];
  const [country, setCountry] = useState<CountryCode>('PT');
  const [national, setNational] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>({ kind: 'phone' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const e164 = toE164(country, national);

  const post = async (path: string, body: unknown) => {
    const r = await fetchImpl(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const b = (await r.json().catch(() => ({}))) as { data?: { token?: string }; error?: { code?: string } };
    return { ok: r.ok, status: r.status, body: b };
  };
  const message = (status: number, codeName?: string) =>
    codeName === 'PHONE_INVALID' ? t.bad : codeName === 'CODE_INVALID' ? t.wrong : status === 429 ? t.many : t.fail;

  const send = async (phone: string, resend = false) => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await post('/v1/me/sms-code', { phone, lang });
      if (!r.ok) return setError(message(r.status, r.body.error?.code));
      setCode('');
      setStep({ kind: 'code', phone, resent: resend });
    } catch {
      setError(t.fail);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (phone: string) => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await post('/v1/me/sms-session', { phone, code });
      if (!r.ok || !r.body.data?.token) return setError(message(r.status, r.body.error?.code));
      onToken(r.body.data.token);
    } catch {
      setError(t.fail);
    } finally {
      setBusy(false);
    }
  };

  if (step.kind === 'code') {
    const shown = `${dialCode(country)} ${formatNational(country, national)}`;
    return (
      <section className="ca-page" aria-label="My bookings">
        <div className="ca3-hero">
          <img src="/v3/phone.png" alt="" />
          <h1 className="ca3-h1">{t.codeTitle}</h1>
        </div>
        <form
          className="ca3-form"
          onSubmit={(ev) => {
            ev.preventDefault();
            void verify(step.phone);
          }}
        >
          <p className="ca3-hint">{t.codeHint(shown)}</p>
          <label htmlFor="me-sms-code">
            {t.code}
            <input
              id="me-sms-code"
              className="ca3-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={10}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
            />
          </label>
          {error && (
            <p className="ca3-error" role="alert">
              {error}
            </p>
          )}
          {step.resent && !error && <p className="ca3-hint">✓</p>}
          <button className="ca3-btn red" disabled={busy || code.length < 4}>
            {busy ? t.checking : t.show}
          </button>
        </form>
        <div className="ca-row ca-pad">
          <button
            className="ca3-link"
            onClick={() => {
              setError(undefined);
              setStep({ kind: 'phone' });
            }}
          >
            ← {t.other}
          </button>
          <button className="ca3-link" disabled={busy} onClick={() => void send(step.phone, true)}>
            {t.again}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="ca-page" aria-label="My bookings">
      <div className="ca3-hero">
        <img src="/v3/t-visits.png" alt="" />
        <h1 className="ca3-h1">{t.title}</h1>
      </div>
      <form
        className="ca3-form"
        onSubmit={(ev) => {
          ev.preventDefault();
          if (e164) void send(e164);
          else setError(t.bad);
        }}
      >
        <label htmlFor="me-phone">
          {t.phone}
          <span className="ca3-phone">
            <select aria-label="Country" value={country} onChange={(e) => setCountry(e.target.value as CountryCode)}>
              {TOP_COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {flagOf(c)} {dialCode(c)}
                </option>
              ))}
            </select>
            <input
              id="me-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              placeholder={examplePhone(country)}
              value={formatNational(country, national)}
              onChange={(e) => setNational(e.target.value)}
            />
          </span>
        </label>
        <p className="ca3-hint">{t.hint}</p>
        {error && (
          <p className="ca3-error" role="alert">
            {error}
          </p>
        )}
        <button className="ca3-btn red" disabled={busy || !e164}>
          {busy ? t.sending : t.send}
        </button>
      </form>
      <div className="ca-row ca-pad">
        <button className="ca3-link" onClick={onEmail}>
          {t.email}
        </button>
      </div>
    </section>
  );
}
